import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImprovedResumeUploadStep } from '@/components/onboarding/steps/ImprovedResumeUploadStep';
import { ProfileReviewStep } from '@/components/onboarding/steps/ProfileReviewStep';
import { GoalsPreferencesStep } from '@/components/onboarding/steps/GoalsPreferencesStep';
import { useToast } from '@/hooks/use-toast';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { Terminal, ShieldCheck, Database, Cpu } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';

export function ImprovedOnboardingPage() {
 const navigate = useNavigate();
 const { toast } = useToast();
 const { refreshProfile } = useAuth();
 const {
  currentStep,
  sessionId,
  resumeData,
  initialize,
  setStep,
  saveStepProgress,
  completeOnboarding,
  isLoading,
  error,
 } = useOnboardingStore();

 // Initialize onboarding session
 useEffect(() => {
  const checkProgress = async () => {
   const response = (await initialize()) as any;
   if (response?.progress?.completionPercentage === 100) {
    navigate('/dashboard');
   }
  };
  checkProgress();
 }, [initialize, navigate]);

 // Handle errors from store
 useEffect(() => {
  if (error) {
   toast({
    title: 'Error',
    description: error,
    variant: 'destructive',
   });
  }
 }, [error, toast]);

 const handleDataExtracted = async (data: any) => {
  if (data) {
   try {
    await saveStepProgress(1, data);
   } catch (err) {
    console.error('Failed to save step 1 progress:', err);
   }
  }
 };

 const handleProfileComplete = async (data: any) => {
  try {
   await saveStepProgress(2, data);
  } catch (error) {
   console.error('Failed to save profile data:', error);
  }
 };

 const handleGoalsComplete = async (data: any) => {
  try {
   await saveStepProgress(3, data);
  } catch (error) {
   console.error('Failed to save goals data:', error);
  }
 };

 const handleNext = async () => {
  if (currentStep < 3) {
   setStep(currentStep + 1);
  } else {
   try {
    await completeOnboarding();
    await refreshProfile();
    toast({
     title: 'System Ready',
     description: 'Profile configuration complete. Initializing dashboard...',
    });
    setTimeout(() => {
     navigate('/dashboard');
    }, 1500);
   } catch (error) {
    console.error('Failed to complete onboarding:', error);
   }
  }
 };

 const handlePrevious = () => {
  if (currentStep > 1) {
   setStep(currentStep - 1);
  }
 };

 const renderStepIndicator = () => (
  <div className='flex items-center gap-4 text-xs font-mono mb-8 border-b border-border pb-4'>
   <div
    className={`flex items-center gap-2 ${
     currentStep >= 1 ? 'text-primary' : 'text-muted-foreground'
    }`}
   >
    <Database className='w-4 h-4' />
    <span className='hidden sm:inline'>1. INGESTION</span>
   </div>
   <div className='w-8 h-px bg-border' />
   <div
    className={`flex items-center gap-2 ${
     currentStep >= 2 ? 'text-primary' : 'text-muted-foreground'
    }`}
   >
    <ShieldCheck className='w-4 h-4' />
    <span className='hidden sm:inline'>2. VERIFICATION</span>
   </div>
   <div className='w-8 h-px bg-border' />
   <div
    className={`flex items-center gap-2 ${
     currentStep >= 3 ? 'text-primary' : 'text-muted-foreground'
    }`}
   >
    <Cpu className='w-4 h-4' />
    <span className='hidden sm:inline'>3. CONFIGURATION</span>
   </div>
  </div>
 );

 return (
  <div className='min-h-screen bg-background text-foreground font-sans p-4 md:p-6'>
   <div className='fixed inset-0 z-0 pointer-events-none'>
    <div className='w-full h-full bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:24px_24px] opacity-20'></div>
   </div>

   <div className='max-w-4xl mx-auto relative z-10'>
    <div className='flex items-center justify-between mb-12'>
     <div className='flex items-center gap-2 font-mono font-bold text-xl'>
      <Terminal className='w-6 h-6 text-primary' />
      <span>MOCK.SYSTEM</span>
     </div>
     <div className='font-mono text-xs text-muted-foreground'>
      SESSION ID:{' '}
      {sessionId ? sessionId.substring(0, 8).toUpperCase() : 'INIT...'}
     </div>
    </div>

    <div className='bg-background border border-border p-4 md:p-8 shadow-xl relative'>
     <div className='absolute top-0 left-0 w-full h-1 bg-primary/20'>
      <div
       className='h-full bg-primary transition-all duration-500'
       style={{ width: `${(currentStep / 3) * 100}%` }}
      />
     </div>

     {renderStepIndicator()}

     <div className='min-h-[400px]'>
      {currentStep === 1 && (
       <ImprovedResumeUploadStep
        onDataExtracted={handleDataExtracted}
        onNext={handleNext}
        onPrevious={currentStep > 1 ? handlePrevious : undefined}
       />
      )}

      {currentStep === 2 && (
       <ProfileReviewStep
        initialData={resumeData || undefined}
        onDataUpdate={handleProfileComplete}
        onNext={handleNext}
        onPrevious={handlePrevious}
       />
      )}

      {currentStep === 3 && (
       <GoalsPreferencesStep
        onDataUpdate={handleGoalsComplete}
        onNext={handleNext}
        onPrevious={handlePrevious}
       />
      )}
     </div>
    </div>

    <div className='mt-8 flex justify-between font-mono text-xs text-muted-foreground'>
     <div>
      STATUS: {isLoading ? 'PROCESSING...' : 'CONFIGURING_USER_PROFILE'}
     </div>
     <div>ENCRYPTION: ENABLED</div>
    </div>
   </div>
  </div>
 );
}
